import { APIPackLine, PackLine, APIDelivery, Delivery, APIRouteWithStatus, RouteWithStatus } from '../types/index.js';
import { appStore } from '../store/appStore.js';

// Adapter functions to convert backend API data to frontend component expected format
export class DataAdapter {

  // Adapt PackLine from API to component expected format
  static adaptPackLineForComponents(apiPackLine: APIPackLine): PackLine {
    // Get the currently linked pack line from the store
    const linkedPackLine = appStore.getState().linkedPackLine;

    // Determine if pack line is actively scanning:
    // - Pack line is not ended (ended_at is null)
    // - Current session is not null, started, and not ended
    // - AND this pack line is the currently linked one
    const isActive = (
      apiPackLine.ended_at === null &&
      apiPackLine.current_session !== null &&
      apiPackLine.current_session.started_at !== null &&
      apiPackLine.current_session.ended_at === null
    );
    // Determine if pack line is on break:
    // - Pack line is not ended (ended_at is null)
    // - Current session is ended or null (meaning break state)
    const isOnBreak = apiPackLine.ended_at === null && (
      apiPackLine.current_session === null ||
      apiPackLine.current_session?.ended_at !== null
    );
    const isLinked = isActive && linkedPackLine?.id === apiPackLine.id || isOnBreak && linkedPackLine?.id === apiPackLine.id;
    const exemptMembers = apiPackLine.employee_exempt_count || 0;
    const tempMembers = apiPackLine.employee_temp_count || 0;
    const memberCount = exemptMembers + tempMembers;

    return {
      // Map API fields to component expected fields
      id: apiPackLine.id,
      lineLeadName: apiPackLine.owner,
      lineNumber: apiPackLine.line_number,
      overallPace: apiPackLine.metrics?.overall_pace || 0,
      currentPace: apiPackLine.metrics?.current_pace || 0,
      currentRoute: apiPackLine.current_route?.name || '',
      bagsPacked: apiPackLine.current_route?.packed_deliveries || 0,
      totalBags: apiPackLine.current_route?.total_deliveries || 0,
      completion: apiPackLine.current_route?.percent_completed || 0,
      qrCode: apiPackLine.qr_code || '',
      isLinked: isLinked,
      isActive: isActive,
      isOnBreak: isOnBreak,
      exemptWorkers: exemptMembers,
      tempWorkers: tempMembers,
      memberCount: memberCount,
      routes: (apiPackLine.routes || []).map((route) => this.adaptRouteForComponents(route)),
      currentSession: apiPackLine.current_session || null,
      metrics: apiPackLine.metrics || null,
    };
  }

  // Adapt Delivery from API to component expected format
  static adaptDeliveryForComponents(apiDelivery: APIDelivery): Delivery {
    return {
      // Map API fields to component expected fields
      id: apiDelivery.id,
      dropNumber: apiDelivery.drop_number,
      name: apiDelivery.recipient_name || '',
      packed: apiDelivery.status === 'scanned' ? 'scanned' :
             apiDelivery.status === 'omitted' ? 'omitted' :
             apiDelivery.status === 'manual_entry' ? 'manual_entry' : 'pending',
      bagCount: apiDelivery.expected_bag_count || 0,
      packTime: apiDelivery.status_submitted_at,
      staffOverride: apiDelivery.status_submitted_by,
    };
  }

  // Adapt Route from API to component expected format
  static adaptRouteForComponents(apiRoute: APIRouteWithStatus): RouteWithStatus {
    return {
      // Map API fields to component expected fields
      id: apiRoute.id,
      name: apiRoute.name,
      routeGroup: '', // API doesn't have route group
      routes: 1, // Default - API doesn't track route count
      drops: apiRoute.total_deliveries,
      deliveries: (apiRoute.deliveries || []).map((delivery: any) =>
        DataAdapter.adaptDeliveryForComponents(delivery)
      ),
      scanned: apiRoute.packed_deliveries,
      start: apiRoute.started_at,
      end: apiRoute.ended_at,
      status: apiRoute.status === 'not_started' ? 'not-started' :
              apiRoute.status === 'in_progress' ? 'in-progress' :
              apiRoute.status === 'completed' ? 'complete' : 'incomplete',
      completion: apiRoute.percent_completed,
      bagTotal: apiRoute.total_deliveries // Approximate
    };
  }
}
